import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { SeperateTagsDropdown, SpreadsheetUtils, WithTooltip } from 'basic-components';
import { Info } from 'basic-components/icons';

import { getNameWithoutType } from '../CSVService';
import MessageContainer from '../MessageContainer';

const thereAreNoDuplicates = columns => Array.from(new Set(columns)).length === columns.length

const DATE_COLUMN = "Date"
const SymbolsStep = ({ initHeader, setIsSucess, setIsError, worksheetRef, ref }) => {
  const [symbols, setSymbols] = useState([]),
        getColumns = useCallback(() => worksheetRef.current?.getColumns(), [worksheetRef]),
        [columns, setColumns] = useState(() => getColumns()),
        [errorMessage, setErrorMessage] = useState(undefined),
        getRows = useCallback(() => worksheetRef.current?.getRows(), [worksheetRef]),
        noTypeColumns = useMemo(() => columns.map(c => getNameWithoutType(c?.columnName)), [columns]),
        dateColumnIndex = useMemo(() => noTypeColumns.indexOf(DATE_COLUMN), [noTypeColumns]),
        columnsLeftToDate = useMemo(() => noTypeColumns.slice(0, dateColumnIndex).filter(c => c !== DATE_COLUMN), [dateColumnIndex, noTypeColumns]),
        columnsRightToDate = useMemo(() => noTypeColumns.slice(dateColumnIndex + 1), [dateColumnIndex, noTypeColumns]),
        dropdownRef = useRef(),
        onChangeSymbols = useCallback((value, { isAdd, newValue }) => formatColumn({ setSymbols, value, getRows, columns, newValue, isAdd, worksheetRef, initHeader, noTypeColumns, setErrorMessage, errorMessage }), [getRows, columns, worksheetRef, initHeader, noTypeColumns, errorMessage]),
        dropdownOptions = useMemo(() => columnsRightToDate.map(c => ({ label: c, value: c })), [columnsRightToDate]),
        [isValid, setIsValid] = useState(false)

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => {
      const columns = getColumns().map(c => c?.columnId),
            noDuplicates = thereAreNoDuplicates(columns)
      if((symbols.length > 0 || columns.indexOf(DATE_COLUMN) > 0) && noDuplicates) {
        setErrorMessage()
        setIsValid(true)
        setIsSucess(true)
        setIsError(false)
        return true;
      } else {
        setIsValid(false)
        setIsSucess(false)
        setIsError(true)
        if(noDuplicates) {
          setErrorMessage("At least one symbol has to be selected.")
        } else {
          setErrorMessage("Column names can not be duplicated.")
        }
        return false;
      }
    },
    isValid: () => isValid,
    setColumns
  }))

  return (
    <div className="ncsv-creator__top__step ncsv-creator__top__step--symbols">
      <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--label">
        <div className="ncsv-creator__top__step__row__label__text">Select one or more symbol columns</div>
        <WithTooltip
            data-place="top"
            text="Symbol is a unique identifier for data record. All symbols will be moved to the left of the Date column. On this step make sure that there are no duplicate column names."
        >
          <div className="ncsv-creator__top__step__row__label__info">
            <Info
                color="blue-bright"
                height={16}
                width={16}
            />
          </div>
        </WithTooltip>
      </div>
      <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--value">
        <SeperateTagsDropdown
            defaultValue={columnsLeftToDate}
            fixed
            hideSelectedOptions
            name="symbolColumns"
            onChange={onChangeSymbols}
            options={dropdownOptions}
            placeholder="Select columns"
            ref={dropdownRef}
        />
      </div>
      <MessageContainer
          errorMessage={errorMessage}
      />
    </div>
  )
}

SymbolsStep.displayName = "SymbolsStep"

SymbolsStep.propTypes = {
  initHeader: PropTypes.array.isRequired,
  setIsError: PropTypes.func.isRequired,
  setIsSucess: PropTypes.func.isRequired,
  worksheetRef: PropTypes.object.isRequired,
}

export default SymbolsStep

const formatColumn = ({ setSymbols, value, getRows, columns, newValue, isAdd, worksheetRef, initHeader, noTypeColumns, setErrorMessage }) => {
  setErrorMessage()
  setSymbols(value)
  const rows = getRows(),
        colIndex = columns.findIndex(c => getNameWithoutType(c?.columnId) === newValue),
        columnId = columns[colIndex]?.columnId,
        dateColumnIndex = columns.findIndex(c => c?.columnId === DATE_COLUMN)
  if (isAdd) {
    addSymbol({ worksheetRef, columnId, newValue, rows, colIndex, columns })
  } else {
    removeSymbol({ newValue, dateColumnIndex, worksheetRef, columns, initHeader, noTypeColumns })
  }
}
const addSymbol = ({ worksheetRef, columnId, newValue, rows, colIndex, columns }) => {
  worksheetRef.current?.handleChanges([
    ...(columnId !== newValue ? columnId !== newValue && rows.filter(row => String(row.rowId).startsWith("header")).map(row => SpreadsheetUtils.getRowChange({ row, colIndex, columnId, newValue })) : []),
    SpreadsheetUtils.getColumnReorderChange({ columnIds: [newValue], targetColumnId: DATE_COLUMN, columns })
  ])
}

const removeSymbol = ({ newValue, dateColumnIndex, worksheetRef, columns, initHeader, noTypeColumns }) => {
  const targetPosition = getPreviousIndex({ initHeader, newValue, columns, dateColumnIndex, noTypeColumns })
  worksheetRef.current?.handleChanges([
    SpreadsheetUtils.getColumnReorderChange({ columnIds: [newValue], targetColumnId: columns[targetPosition]?.columnId, columns })
  ])
}

const getPreviousIndex = ({ initHeader, newValue, columns, dateColumnIndex, noTypeColumns }) => {
  const initIndex = initHeader.indexOf(newValue)
  const nextColumnIndex = columns.findIndex((column, i) => {
    if(i > dateColumnIndex) {
      const oldIndex = initHeader.indexOf(column?.columnId)
      return oldIndex > initIndex
    }
  })
  const toReturn = nextColumnIndex !== -1 ? nextColumnIndex - 1 : noTypeColumns.length - 1
  return toReturn
}
